import * as React from 'react';

import { DetailsTableCell } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableCell';
import { CustomSingleCellDetailsTableRow } from '@amzn/imdb-shared-meridian-components/components/DetailsTable/DetailsTableRow';
import { NewWindowLink } from '@amzn/imdb-shared-meridian-components/components/link';
import Column from '@amzn/meridian/column';
import { ColumnWidth } from '@amzn/meridian/column/column';
import Link from '@amzn/meridian/link';
import Table from '@amzn/meridian/table';

import { ListAuthorDetails } from '../../../listoramaAdmin-api/generated-src/api';
import { getUserAdminUrlForAuthor, getAuthorLink } from '../../../utils/url';

type AuthorDetailsCellId =
    | 'ID'
    | 'Nickname'
    | 'Created'
    | 'Status'
    | 'Is List Restricted?'
    | 'List Restricted Reason'
    | 'List Restricted Timestamp'
    | 'Is Pro?';

interface AuthorDetailsTableProps {
    author: ListAuthorDetails;
    width?: ColumnWidth;
}

export const TEST_ID = {
    USER_ADMIN_LINK: 'userAdminLink',
    LOR_ADMIN_LINK: 'lorAdminLink',
    USER_ID: 'userId',
    NICK_NAME: 'nickname',
    CREATED_DATE: 'createdDate',
    STATUS: 'status',
    IS_LIST_RESTRICTED: 'isListRestricted',
    LIST_RESTRICTED_REASON: 'listRestrictedReason',
    LIST_RESTRICTED_TIMESTAMP: 'listRestrictedTimestamp',
    IS_PRO: 'isPro'
};

export const AuthorDetailsTable: React.FC<AuthorDetailsTableProps> = (props) => {
    const { author, width } = props;
    const titleDetailsTableRows = createTableRows(author);

    return (
        <Column width={width ? width : '100%'}>
            <Table
                data-test-id='authorDetailsTable'
                showDividers={true}
                spacing='small'
                rowComponents={[CustomSingleCellDetailsTableRow]}
            >
                {titleDetailsTableRows.map((row) => (
                    <CustomSingleCellDetailsTableRow
                        key={row.name}
                        header={row.name}
                        data={row.data}
                        getTableCell={(cellId, data) =>
                            getAuthorDetailsTableCell(cellId as AuthorDetailsCellId, data, row.testid)
                        }
                        data-testid={row.testid}
                    />
                ))}
            </Table>
        </Column>
    );
};

interface CustomTableRowProps<T extends string> {
    name: T;
    data: string;
    testid: string;
}

const getAuthorDetailsTableCell = (cellId: AuthorDetailsCellId, data: string, testid: string) => {
    switch (cellId) {
        case 'ID':
            return (
                <DetailsTableCell
                    CustomDataElement={
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Link href={getAuthorLink(data)} data-testid={TEST_ID.LOR_ADMIN_LINK}>
                                {data}
                            </Link>
                            <span>:</span>
                            <NewWindowLink
                                url={getUserAdminUrlForAuthor(data)}
                                data-testid={TEST_ID.USER_ADMIN_LINK}
                                text={'UDS'}
                            />
                        </div>
                    }
                />
            );
        default:
            return <DetailsTableCell data={data} data-testid={testid} />;
    }
};

const createTableRows = (author: ListAuthorDetails): CustomTableRowProps<AuthorDetailsCellId>[] => {
    const rows: CustomTableRowProps<AuthorDetailsCellId>[] = [
        {
            name: 'ID',
            data: author.authorId,
            testid: TEST_ID.USER_ID
        },
        {
            name: 'Nickname',
            data: author.nickname,
            testid: TEST_ID.NICK_NAME
        },
        {
            name: 'Created',
            data: author.registrationDate,
            testid: TEST_ID.CREATED_DATE
        },
        {
            name: 'Status',
            data: author.status,
            testid: TEST_ID.STATUS
        },
        {
            name: 'Is Pro?',
            data: author.isPro ? 'Yes' : 'No',
            testid: TEST_ID.IS_PRO
        },
        {
            name: 'Is List Restricted?',
            data: author.isShadowbanned ? 'Yes' : 'No',
            testid: TEST_ID.IS_LIST_RESTRICTED
        }
    ];

    if (author.isShadowbanned) {
        rows.push(
            {
                name: 'List Restricted Reason',
                data: author.shadowbannedReason || '',
                testid: TEST_ID.LIST_RESTRICTED_REASON
            },
            {
                name: 'List Restricted Timestamp',
                data: author.shadowbannedTimestamp || '',
                testid: TEST_ID.LIST_RESTRICTED_TIMESTAMP
            }
        );
    }
    return rows;
};
